import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { ROUTES_NAMES } from 'constants/name.routes'
import { notificationStatus } from 'constants/notificationStatus'
import { useEffect, useMemo, useState } from 'react'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Dispatch } from 'redux'
import NotificationActions from 'actions/Notification'
import NotificationService from 'services/Notification'
import { NotificationAction, NotificationReducer } from 'types/notification'
import { RootReducerState } from 'types/global'
import { EditButton } from '../../NotificationsTreatmentsSteps/IshikawaDiagram/styles'
import {
    HelpDescription,
    Subtitle,
    Title,
    checkboxStyle,
    notPerformStyle
} from '../../NotificationsTreatmentsSteps/LondonProtocol/styles'
import { GroupTreatmentsStep4Props } from '../GroupActionPlan/helpers'
import {
    buildLondonProtocolSysIdByNotificationMap,
    convertLondonNotPerformCreateBatchForNotificationList,
    convertLondonNotPerformUpdateBatchRequest,
    getGroupNotificationSysId,
    getLondonProtocolRecordId,
    isGroupLondonProtocolOptional,
    isLondonDoNotPerformOptionalFromProtocol,
    mergeFirstLondonProtocolIdFromRedux,
    partitionNotificationsByProtocolMap
} from './helpers'
import StaticDataVersioned from '../../NotificationsTreatmentsSteps/LondonProtocol/Version/Static'
import PrintContainer from '../../NotificationsTreatmentsSteps/components/PrintContainer'

export const canEditGroupLondonProtocol = ({
    loggedInUserIsResponsible,
    isSameLocation,
    isSameSector,
    notificationStatusState
}: {
    loggedInUserIsResponsible: boolean
    isSameLocation: boolean
    isSameSector: boolean
    notificationStatusState: string
}) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [listOfRoles.admin, listOfRoles.location_qa]
    }

    return [
        ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
        ...(isSameLocation && isSameSector && loggedInUserIsResponsible
            ? [listOfRoles.location_manager]
            : []),
        ...(isSameLocation && loggedInUserIsResponsible
            ? [listOfRoles.location_qa]
            : [])
    ]
}

const GroupLondonProtocol = ({
    id,
    tooltipAlert: _tooltipAlertShownInParent,
    currentUser,
    statusIsArchivedOrFinished,
    notificationById,
    notificationStatusState,
    groupData,
    loggedInUserIsResponsible = true
}: GroupTreatmentsStep4Props) => {
    const navigate = useNavigate()
    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()
    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )
    const [notAllowed, setNotAllowed] = useState(false)
    /** Quando não é null, reflete o checkbox após sucesso do batch; evita trava se o GET ficar pending */
    const [notPerformOverride, setNotPerformOverride] = useState<boolean | null>(null)

    useEffect(() => {
        if (groupData?.notifications?.length) {
            const firstNotificationId = getGroupNotificationSysId(groupData.notifications[0])
            if (firstNotificationId) {
                NotificationActions.getLondonProtocolByNotificationId(notificationDispatch, firstNotificationId)
            }
        }
    }, [groupData, notificationDispatch])

    useEffect(() => {
        setNotPerformOverride(null)
    }, [id])

    // Só remove o otimista quando o Redux tiver o mesmo boolean (GET refletiu o batch). Se ainda vier
    // valor antigo, manter o override e não "remarcar" o checkbox
    useEffect(() => {
        if (notPerformOverride === null) return
        if (isLondonDoNotPerformOptionalFromProtocol(notificationSelector?.londonProtocol) === notPerformOverride) {
            setNotPerformOverride(null)
        }
    }, [notificationSelector?.londonProtocol, notPerformOverride])

    const isSameLocation = useMemo(() => {
        if (notificationById?.location_incident?.value) {
            return (
                currentUser?.locations &&
                currentUser.locations.filter((i) => i.sys_id === notificationById.location_incident?.value).length > 0
            )
        }
        return true
    }, [currentUser, notificationById])

    const isSameSector = useMemo(() => {
        if (notificationById?.location_incident?.value && notificationById?.incident_sector?.value) {
            return (
                currentUser?.locations &&
                currentUser.locations
                    .filter((i) => i.sys_id === notificationById.location_incident?.value)[0]
                    ?.sectors.filter((i) => i.sys_id === notificationById.incident_sector?.value).length > 0
            )
        }
        return true
    }, [currentUser, notificationById])

    useEffect(() => {
        const allowed = canEditGroupLondonProtocol({
            loggedInUserIsResponsible,
            isSameLocation,
            isSameSector,
            notificationStatusState
        }).includes(currentUser?.profile)

        setNotAllowed(!(allowed && !statusIsArchivedOrFinished))
    }, [
        loggedInUserIsResponsible,
        isSameLocation,
        isSameSector,
        notificationStatusState,
        currentUser?.profile,
        statusIsArchivedOrFinished
    ])

    const isDoNotPerformChecked =
        notPerformOverride !== null
            ? notPerformOverride
            : isLondonDoNotPerformOptionalFromProtocol(notificationSelector?.londonProtocol)

    const isOptional = useMemo(() => isGroupLondonProtocolOptional(groupData), [groupData])

    const groupKeyDisplay = useMemo(() => {
        const fromInfo = groupData?.group_info?.u_number?.display_value?.trim()
        const fromKey = typeof groupData?.group_key === 'string' ? groupData.group_key.trim() : ''
        return fromInfo || fromKey || ''
    }, [groupData])

    const londonRecordId = getLondonProtocolRecordId(notificationSelector?.londonProtocol)

    const onChangeCheckedNotPerform = async (checkedValue: boolean) => {
        if (!groupData) return

        try {
            // Mesma lógica do individual: decide create vs update pelo que existe no serviço (mapa), não só pelo Redux
            const protocolIdByNotif = mergeFirstLondonProtocolIdFromRedux(
                groupData,
                await buildLondonProtocolSysIdByNotificationMap(groupData),
                notificationSelector.londonProtocol
            )
            const { withProtocol, withoutProtocol } = partitionNotificationsByProtocolMap(
                groupData,
                protocolIdByNotif
            )

            let londonSource: Record<string, any> = notificationSelector.londonProtocol || {}
            if (withProtocol.length > 0 && Object.keys(londonSource).length === 0) {
                const firstId = getGroupNotificationSysId(groupData.notifications[0])
                if (firstId) {
                    const row = await NotificationService.getLondonProtocolRowByNotificationId(firstId)
                    if (row) londonSource = row
                }
            }

            if (withProtocol.length > 0) {
                await NotificationActions.updateLondonProtocolBatch(
                    notificationDispatch,
                    convertLondonNotPerformUpdateBatchRequest(
                        withProtocol,
                        londonSource,
                        checkedValue,
                        protocolIdByNotif
                    )
                )
            }
            if (withoutProtocol.length > 0) {
                await NotificationActions.createLondonProtocolBatch(
                    notificationDispatch,
                    convertLondonNotPerformCreateBatchForNotificationList(
                        withoutProtocol,
                        checkedValue
                    )
                )
            }

            setNotPerformOverride(checkedValue)

            const firstNotificationId = getGroupNotificationSysId(groupData.notifications[0])
            if (firstNotificationId) {
                try {
                    await NotificationActions.getLondonProtocolByNotificationId(
                        notificationDispatch,
                        firstNotificationId
                    )
                } catch (refetchError) {
                    console.error('Erro ao recarregar Protocolo de Londres após o batch:', refetchError)
                }
            }
        } catch (error) {
            console.error('Erro ao alterar preenchimento do Protocolo de Londres em grupo:', error)
        }
    }

    return (
        <div data-testid="test-group-london-protocol" className="overflow-x-auto">
            <div className="px-8 flex flex-row gap-12 mt-4 min-h-[500px]">
                <fieldset disabled={notAllowed}>
                    <div className="flex flex-col">
                        <Title>Protocolo de Londres</Title>
                        <Subtitle>
                            Ao preencher este protocolo, reconstruímos com detalhes todo o contexto da situação que levou ao incidente.
                            O seu objetivo é garantir que todas as suposições sejam levantadas, para auxiliar na reflexão das oportunidades de melhorias dos processos.
                        </Subtitle>
                        <HelpDescription>
                            Preenchimento facultativo no tratamento em grupo.
                        </HelpDescription>

                        <div className="flex flex-row gap-5 mt-6 mb-10">
                            <EditButton
                                data-testid="test-go-to-forum-button-london-group"
                                type="button"
                                onClick={() => navigate(`${ROUTES_NAMES.notifications}/group_london_protocol/${id}`)}
                                disabled={isDoNotPerformChecked || notAllowed}
                                className={`${isDoNotPerformChecked || notAllowed ? 'bg-disabled text-textSelect font-normal' : ''}`}
                            >
                                <div className="w-[202px] flex justify-center items-center gap-3">
                                    <MdOutlineModeEditOutline
                                        size={18}
                                        color={`${!isDoNotPerformChecked && !notAllowed ? 'white' : '#4F4F4F'}`}
                                    />
                                    <span className="inline-block align-middle">
                                        {!londonRecordId ? 'Preencher protocolo' : 'Editar protocolo'}
                                    </span>
                                </div>
                            </EditButton>

                            {!!londonRecordId &&
                                !!notificationSelector?.londonProtocol?.date_of_patient_admission && (
                                    <PrintContainer
                                        title="Protocolo de Londres"
                                        buttonLabel="Exportar PDF"
                                        disabled={false}
                                        containerId="staticDataContainer"
                                    />
                                )}

                            {isOptional && (
                                <div
                                    data-testid="test-toggle-button-london-group"
                                    className="mt-2 block min-h-[1.5rem] pl-[1.5rem]"
                                >
                                    <input
                                        onChange={(e) => onChangeCheckedNotPerform(e.target.checked)}
                                        checked={isDoNotPerformChecked}
                                        name="do_not_perform_optional"
                                        className={`${checkboxStyle} ${notAllowed ? 'opacity-40' : 'hover:cursor-pointer'}`}
                                        type="checkbox"
                                        id="checkUnrealizedOptionalLondonGroup"
                                    />
                                    <label
                                        className={`${notPerformStyle} ${notAllowed ? 'opacity-40' : 'hover:cursor-pointer'}`}
                                        htmlFor="checkUnrealizedOptionalLondonGroup"
                                    >
                                        Não realizar o preenchimento
                                    </label>
                                </div>
                            )}
                        </div>

                        {!!londonRecordId && !!notificationSelector?.londonProtocol?.date_of_patient_admission && (
                            <StaticDataVersioned
                                londonProtocol={notificationSelector?.londonProtocol}
                                context="group"
                                groupKeyDisplay={groupKeyDisplay}
                            />
                        )}
                    </div>
                </fieldset>
            </div>
        </div>
    )
}

export { GroupLondonProtocol }
